import { useReducer, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import Typography from '@mui/material/Typography';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { nanoid } from 'nanoid';
import Input from 'common/ui/Input';
import { Table } from 'common/Table';
import { Button } from 'common/ui/Button';
import { Colors, TextCss } from 'styles';
import { Transforms, useEditor, ReactEditor, useSelected, Editor, Node } from '../../../core';
import { find, fragmentToString } from '../../../utils';
import type { ColumnDef } from '@tanstack/react-table';
import { INLINE_BOOKMARK_PLUGIN_ID } from '../../inlineBookmark/types';
import { PICKLIST_PLUGIN_ID } from '../types';
import { useCompactUI } from 'hooks/useCompactUI';

import {
  RichTextEditor,
  ParagraphPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  PlaceholderPlugin,
  InlineBookmarkPlugin,
  ClearFormattingPlugin,
  DictaphoneControlsPlugin,
  DictationPlugin,
  BoldPlugin,
  FieldContentWrapper,
  insertSelectedPicklistItem,
} from 'domains/reporter/RichTextEditor';
import { useRecorder } from 'common/Recorder/useRecorder';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { createParagraph } from '../../paragraph/utils';
import type { ReportPicklistBeforeSubmissionOptions } from '../types';
import type { SlateContent } from '../../../../Reporter/types';
import { SlateContextProvider } from 'domains/reporter/Reporter/SlateContext';
import { telemetryContextFor } from 'modules/Apollo/types';
import { rootTheme } from 'theme/rootTheme';

type PicklistBuilderFormProps = {
  options: Array<ReportPicklistBeforeSubmissionOptions>;
  state: any;
  onSave: (props: {
    picklistID: string;
    options: Array<ReportPicklistBeforeSubmissionOptions>;
  }) => Nullable<void>;
  onRemove: (props: { readonly picklistID: string }) => Nullable<void>;
  setOpen: (arg1: boolean) => void;
  picklistID: string;
  styles: Record<any, any>;
};

type PicklistField = ReportPicklistBeforeSubmissionOptions;
type PicklistFieldAction =
  | { action: 'toggle'; value: PicklistField }
  | { action: 'update'; value: PicklistField }
  | { action: 'reset' }
  | { action: 'sync'; value: PicklistField[] };

type PicklistTableRow = PicklistField & { displayText: string };

export const PicklistBuilderForm = ({
  options,
  styles,
  state,
  onSave,
  onRemove,
  setOpen,
  picklistID,
}: PicklistBuilderFormProps): React.ReactElement | null => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [text, setText] = useState<SlateContent>([createParagraph()]);
  const [error, setError] = useState('');
  const editor = useEditor();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const nvoqCredentials = data?.me?.nvoqCredentials;
  const isFocused = useSelected();
  const { setRecorderTarget } = useRecorder();
  const { showCompactHeight } = useCompactUI();

  useEffect(() => {
    setRecorderTarget('newMacro');

    return () => {
      setRecorderTarget('reporter');
    };
  }, [setRecorderTarget]);

  const [fields, fieldAction] = useReducer(
    (current: PicklistField[], action: PicklistFieldAction): PicklistField[] => {
      switch (action.action) {
        case 'sync':
          return action.value;
        case 'toggle':
          return current.some((f) => f.id === action.value.id)
            ? current.filter((f) => f.id !== action.value.id)
            : [...current, action.value];
        case 'update':
          return current.map((f) => (f.id === action.value.id ? { ...f, ...action.value } : f));
        case 'reset':
          return [];
        default:
          return current;
      }
    },
    options
  );

  useEffect(() => {
    if (options) {
      fieldAction({ action: 'sync', value: options });
    }
  }, [options]);

  const getOption = useCallback(
    ({
      validate = false,
    }: {
      validate?: boolean;
    } = {}) => {
      if (formRef.current == null || fields == null || !Array.isArray(fields)) {
        return null;
      }

      const formData = new FormData(formRef.current);
      const optionName = String(formData.get('name') ?? '').trim();
      const hasText =
        Array.isArray(text) && text.some((node) => Node.string(node).trim().length > 0);
      const isDuplicateName = !!optionName && fields.some((f) => f.name === optionName);

      if (hasText && optionName && isDuplicateName !== true) {
        if (validate) {
          setError('');
        }

        return { id: nanoid(), name: optionName, text, default: false };
      } else {
        if (validate) {
          const errorText =
            isDuplicateName === true
              ? 'Please enter a unique name for your picklist in order to save.'
              : !hasText
                ? 'Please enter text for your picklist in order to save.'
                : !optionName
                  ? 'Please enter a name for your picklist in order to save.'
                  : '';

          setError(errorText);
        }
        return null;
      }
    },
    [text, fields]
  );

  const handleAddField = useCallback(
    (evt: FormEvent<HTMLFormElement>) => {
      evt && evt.preventDefault();
      evt && evt.stopPropagation();
      const option = getOption({ validate: true });

      if (option) {
        fieldAction({
          action: 'toggle',
          value: option,
        });
        formRef.current?.reset();
      }
    },
    [fieldAction, getOption]
  );

  const columns = useMemo<Array<ColumnDef<PicklistTableRow>>>(
    () => [
      { header: '#', accessorFn: (_row, index) => index + 1, size: 2 },
      {
        header: 'Name',
        accessorFn: (row) => row.name ?? 'N/A',
        size: 4,
      },
      {
        header: 'Insert text',
        accessorKey: 'displayText',
        size: 15,
      },
      {
        header: () => null,
        accessorKey: 'id',
        cell: ({ row }) => (
          <IconButton
            data-testid="picklist-builder-option-button"
            size="small"
            onClick={() =>
              fieldAction({
                action: 'toggle',
                value: (({ displayText: _displayText, ...rest }) => rest)(row.original),
              })
            }
          >
            <RemoveCircleIcon
              css={`
                ${TextCss.body1}
                margin: 0.2rem;
                color: ${Colors.red6};
              `}
            />
          </IconButton>
        ),
        size: 3,
      },
    ],
    []
  );

  const handleClose = useCallback(
    ({ source }: { source: 'save' | 'cancel' }) => {
      // - Submit: If there are options and no fields: Bookmark
      // - Submit: If there are no options and no fields: Bookmark
      // - Submit: If there are no options and fields: Picklist
      // - Cancel: If there are options and no fields: Picklist
      // - Cancel: If there are no options and no fields: Bookmark
      // - Cancel: If there are no options and fields: Bookmark
      const activeEntry = find(editor, (n) => n.type === PICKLIST_PLUGIN_ID);
      if (
        (source === 'save' && options.length > 0 && fields.length === 0) ||
        (source === 'save' && options.length === 0 && fields.length === 0) ||
        (source === 'cancel' && options.length === 0 && fields.length === 0) ||
        (source === 'cancel' && options.length === 0 && fields.length > 0)
      ) {
        if (activeEntry) {
          Transforms.setNodes(
            editor,
            { type: INLINE_BOOKMARK_PLUGIN_ID, picklistID: null },
            { at: activeEntry[1] }
          );

          onRemove && onRemove(activeEntry[0]);
        }
      }

      if (activeEntry && source === 'save' && fields.length > 0) {
        insertSelectedPicklistItem(editor, activeEntry, fields[0].text);
      }

      ReactEditor.focus(editor);

      // On cancel, return focus to where the user had the cursor
      // On submit, return to the right inside edge of the bracket
      // Submit is taken care of due to the transforms running on the editor
      // inserting the text
      if (source === 'cancel' && editor.selection != null) {
        Transforms.select(editor, editor.selection);
      }
      Transforms.move(editor, { distance: 1, unit: 'offset' });
      setOpen(false);
      setError('');
      fieldAction({ action: 'reset' });
    },
    [editor, options.length, fields, setOpen, onRemove]
  );

  const handleSave = useCallback(() => {
    if (fields == null || !Array.isArray(fields)) {
      return;
    }

    // automatically add any pending field, since the user may easily forget to press
    // the "add" button and directly click on the "Done" button instead
    const pendingOption = getOption();

    onSave && onSave({ picklistID, options: pendingOption ? [...fields, pendingOption] : fields });
    handleClose({ source: 'save' });
  }, [fields, getOption, handleClose, picklistID, onSave]);

  const handleCancel = useCallback(() => {
    handleClose({ source: 'cancel' });
  }, [handleClose]);

  const plugins = useMemo(() => {
    if (
      nvoqCredentials == null ||
      nvoqCredentials.username == null ||
      nvoqCredentials.password == null
    ) {
      return [];
    }

    return [
      DictationPlugin({
        nvoqID: nvoqCredentials?.username,
        nvoqAuthorization: nvoqCredentials?.password,
        target: 'newMacro',
      }),
      ParagraphPlugin(),
      BoldPlugin(),
      ItalicPlugin(),
      UnderlinePlugin(),
      InlineBookmarkPlugin(),
      PlaceholderPlugin({
        onPlaceholderMenuClick: ({ editor, pluginID, placeholderField, instance }) => {
          Transforms.insertNodes(editor, [
            {
              type: pluginID,
              placeholderID: placeholderField.key,
              children: [{ text: placeholderField.name }],
            },
          ]);

          if (!editor.selection) return;
          const nextSelection = Editor.after(editor, editor.selection, { unit: 'offset' });

          if (nextSelection != null) {
            Transforms.select(editor, nextSelection);
          }
          instance.hide();
        },
      }),
      ClearFormattingPlugin(),
      DictaphoneControlsPlugin(),
    ];
  }, [nvoqCredentials]);

  return (
    <div
      css={`
        display: grid;
        flex: 1;
        grid-template-rows: minmax(max-content, 0) minmax(max-content, 0) auto minmax(
            max-content,
            0
          );
        grid-row-gap: ${showCompactHeight ? 0.5 : 2}rem;
        padding: 1rem 0;
      `}
    >
      <Typography variant="h5">Insert Field</Typography>
      <form ref={formRef} onSubmit={handleAddField}>
        <div
          css={`
            margin-bottom: 2rem;
          `}
        >
          <Typography variant="body2" css="margin-left: 10px; margin-bottom: 1.5rem;">
            Name
          </Typography>

          <Input
            name="name"
            data-testid="picklist-input-name"
            required
            pattern=".*\S.*"
            title="Please enter a name for your field in order to save"
            autoComplete="off"
            css={`
              width: 100%;
            `}
          />
        </div>

        <div
          css={`
            display: grid;
            grid-column-gap: 1rem;
            grid-row-gap: 1rem;
            align-items: center;
            grid-template-columns: 2fr /* 1fr */ auto;
          `}
        >
          <Typography variant="body2" css="margin-left: 10px;">
            Insert text
          </Typography>
          <span />
          <div
            data-testid="picklist-rich-text-field"
            css={`
              padding: 6px 0 16px;
            `}
          >
            <FieldContentWrapper isFocused={isFocused}>
              <SlateContextProvider plugins={plugins} initialValue={text}>
                <RichTextEditor variant="fragment" onChange={setText} initialValue={text} />
              </SlateContextProvider>
            </FieldContentWrapper>
          </div>

          <IconButton type="submit" data-testid="addPicklistItem" size="large">
            <AddCircleIcon />
          </IconButton>
        </div>

        <div
          css={`
            margin-left: 0.6rem;
            color: ${Colors.red6};
          `}
        >
          {error}&nbsp;
        </div>
      </form>
      <form onSubmit={(evt) => evt.preventDefault()} css="display: contents;">
        <Table
          columns={columns}
          data={(fields ?? []).map(
            (field): PicklistTableRow => ({
              ...field,
              displayText: fragmentToString(field.text),
            })
          )}
          css={`
            background-color: ${Colors.gray2};
            margin: 0 0.5rem;
            border-radius: ${rootTheme.radius.sm}px;
            padding: 1rem;
          `}
        />
      </form>
      <div css="display: grid; grid-template-columns: auto auto; justify-content: flex-end; grid-column-gap: 10px;">
        <Button variant="ghost" onClick={handleCancel} data-testid="cancel-picklist">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} data-testid="done">
          Done
        </Button>
      </div>
    </div>
  );
};

PicklistBuilderForm.displayName = 'PicklistBuilderForm';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(PicklistBuilderForm);
